import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import logo from "../../assets/image/logo.svg";
import { Utensils, Phone, MapPin, Clock, CalendarX, Facebook, Instagram, ChevronDown } from "lucide-react";
import pizza from "../../assets/image/pizza.jpg";
import aperitivo from "../../assets/image/aperitivo.jpg";
import tagliere from "../../assets/image/tagliere.jpg";
import FramedImage from "../../components/framed/FramedImage";
import Lottie from "lottie-react";
import beerBubbles from "../../assets/lottie/beer-bubbles.json";
import { useNavigate } from "react-router-dom";
import PartnerCarousel from "../../components/carousel/PartnerCarousel";
import aic from "../../assets/partner/aic.webp"
import CustomerReviews from "../../section/review/CustomerReviews";
import PizzaioliSection from "../../section/piazzaioli/PizzaioliSection";
import ImpastoSection from "../../section/impasto/ImpastoSection";

const Home = () => {
	const navigate = useNavigate()
	const menuButtonRef = useRef(null);
	//const url = "https://ilbalconcinofoodbeer.us21.list-manage.com/subscribe/post?u=0dc1a5b547346e0502fafa761&amp;id=53441350c2&amp;f_id=00b8f4e6f0";
	const [isSticky, setIsSticky] = useState(false);
	const [showVideo, setShowVideo] = useState(false);
	const [isMobile, setIsMobile] = useState(false);

	useEffect(() => {
		const isMob = window.innerWidth < 640;
		if (isMob) {
			setIsMobile(true);
		}
	}, []);


	useEffect(() => {
		const timeout = setTimeout(() => setShowVideo(true), 500); // o suIntersectionObserver
		return () => clearTimeout(timeout);
	}, []);

	useEffect(() => {
		const target = menuButtonRef.current;

		const observer = new IntersectionObserver(
			([entry]) => {
				setIsSticky(!entry.isIntersecting); // sticky solo se il bottone NON è visibile
			},
			{ threshold: 0.1 }
		);

		if (target) {
			observer.observe(target);
		}

		return () => {
			if (target) {
				observer.unobserve(target);
			}
		};
	}, []);

	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.visibilityState === "visible") {
				const videos = document.querySelectorAll("video");
				videos.forEach((video) => {
					if (video.paused && video.readyState >= 2) {
						video.play().catch(() => { });
					}
				});
			}
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);
		return () => {
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, []);

	return (
		<main className="bg-background text-secondary font-sans w-full overflow-x-hidden">
			{/* Header */}
			<header
				className={`fixed top-0 left-0 w-full z-50 py-4 px-6 flex items-center justify-center transition-all duration-300 
							${isSticky ? "bg-surface shadow-md opacity-100" : "opacity-0 pointer-events-none"}`}
			>
				<AnimatePresence>
					{isSticky && (
						<motion.img
							src={logo}
							alt="Logo"
							initial={{ opacity: 0, x: -10 }}
							animate={{ opacity: 1, x: 0 }}
							exit={{ opacity: 0, x: -10 }}
							transition={{ duration: 0.3 }}
							className="absolute left-6 h-12 md:h-10"
						/>
					)}
				</AnimatePresence>

				<a
					href="/menu"
					className="flex items-center gap-2 border border-primary text-primary font-semibold px-5 py-2 rounded-full hover:bg-primary hover:text-black transition"
				>
					<Utensils className="w-5 h-5" />
					Vedi Menù
				</a>
			</header>



			{/* Hero */}
			<section className="relative w-full h-safe-screen flex flex-col items-center justify-center text-center overflow-hidden bg-background">
				{/* Background image */}
				{showVideo && (
					<video
						autoPlay
						muted
						loop
						preload="auto"
						playsInline
						draggable={false}
						className="absolute inset-0 w-full h-full object-cover opacity-70"
					>
						<source
							src={
								isMobile ?
								"/videos/preparazione_mobile.mp4"
								:
								"/videos/preparazione_desktop.mp4"
							}
							type="video/mp4"
						/>
						Il tuo browser non supporta i video HTML5.
					</video>
				)}

				{/* Logo */}
				<motion.img
					src={logo}
					alt={"Logo"}
					className="w-56 mb-4 relative z-10 drop-shadow-lg"
					loading="lazy"
					initial={{ opacity: 0, y: 0 }}
					animate={{ opacity: 1, y: 20 }}
					transition={{ delay: 0.5 }}
					draggable={false}
				/>

				{/* CTA */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.5 }}
					className="z-10 mt-6"
				>
					<a
						ref={menuButtonRef}
						href="/menu"
						className="bg-primary text-black font-semibold px-6 py-2 rounded-full hover:scale-105 transition flex items-center gap-2"
					>
						<Utensils className="w-5 h-5" />
						Vedi Menù
					</a>
				</motion.div>
				<motion.div
					initial={{ opacity: 0, y: 10 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 1 }}
					className="absolute text-white text-center z-10"
					style={{
						bottom: 'calc(1.5rem + env(safe-area-inset-bottom))' // 1.5rem = ~bottom-6
					}}
				>
					<a href="#filosofia" className="text-lg font-semibold flex flex-col items-center">
						<span>Scopri di più</span>
						<ChevronDown className="mt-1 animate-bounce w-6 h-6" />
					</a>
				</motion.div>
			</section>

			<PizzaioliSection />

			<section className="bg-surface border border-accent px-6 py-16 text-center max-w-4xl mx-auto rounded-2xl shadow-[0_0_30px_rgba(233,69,69,0.1)] my-10">
				<motion.h2
					className="text-2xl md:text-3xl font-title text-primary mb-4 tracking-tight"
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.6 }}
				>
					Siamo associati AIC - Associazione Italiana Celiachia
				</motion.h2>

				<motion.p
					className="text-muted text-base md:text-lg leading-relaxed mb-6"
					initial={{ opacity: 0 }}
					whileInView={{ opacity: 1 }}
					transition={{ delay: 0.2, duration: 0.6 }}
				>
					Garantiamo attenzione e sicurezza nella preparazione di piatti senza glutine, seguendo i protocolli approvati da AIC per offrire un’esperienza serena anche alle persone celiache.
				</motion.p>

				<motion.div
					initial={{ opacity: 0, scale: 0.95 }}
					whileInView={{ opacity: 1, scale: 1 }}
					transition={{ delay: 0.3, duration: 0.5 }}
					className="flex justify-center"
				>
					<a
						href="https://www.celiachia.it/"
						target="_blank"
						rel="noreferrer"
						aria-label="Visita il sito AIC"
					>
						<img
							src={aic}
							alt="Logo AIC - Associazione Italiana Celiachia"
							className="h-16 md:h-20 object-contain mx-auto grayscale hover:grayscale-0 transition"
						/>
					</a>
				</motion.div>
			</section>

			<ImpastoSection />

			{/* Servizi */}
			<section className="space-y-20 py-20 px-6 max-w-6xl mx-auto">
				{[{
					title: "Pizze Gourmet",
					desc: "Classiche e gourmet, tutte realizzate con ingredienti di prima scelta.",
					image: pizza,
					frame: "label"
				}, {
					title: "I nostri taglieri",
					desc: "Selezioni di salumi e formaggi pregiati tra cui scegliere.",
					image: tagliere,
					frame: "label",
				}, {
					title: "I nostri antipasti",
					desc: "Dalla nostra tradizione ai più gustosi fritti americani.",
					image: aperitivo,
					frame: "label"
				}].map((item, idx) => (
					<motion.div
						key={idx}
						className={`flex flex-col lg:flex-row ${idx % 2 !== 0 ? "lg:flex-row-reverse" : ""} gap-6 items-center lg:items-center`}
						initial={{ opacity: 0, y: 30 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6 }}
					>
						{/* Immagine */}
						<div className="w-full lg:w-1/2">
							<FramedImage src={item.image} alt={item.title} variant={item.frame} label={item.label} />
						</div>

						{/* Testo */}
						<div className="w-full lg:w-1/2 text-left">
							<h3 className="text-xl font-title text-primary tracking-tight leading-snug mb-2">
								{item.title}
							</h3>
							<p className="text-gray-300">{item.desc}</p>
						</div>
					</motion.div>
				))}
			</section>

			<section className="relative w-full min-h-[80vh] flex items-center justify-center overflow-hidden bg-black">
				{/* Animazione bolle */}
				<Lottie
					animationData={beerBubbles}
					loop
					className="absolute bottom-0 w-full min-h-[80vh] opacity-100 pointer-events-none"
				/>

				{/* Overlay rosso */}
				<div className="absolute inset-0 bg-primary/50 z-0 pointer-events-none" />

				{/* Background Video */}
				<video
					autoPlay
					muted
					loop
					playsInline
					className="absolute inset-0 w-full h-full object-cover opacity-70"
				>
					<source src="/videos/birra.mp4" type="video/mp4" />
					Il tuo browser non supporta i video HTML5.
				</video>

				{/* Contenuto con box visibile */}
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.6 }}
					className="relative z-10 px-6 max-w-3xl"
				>
					<div className="bg-black/70 backdrop-blur-sm rounded-2xl p-8 text-center shadow-xl">
						<h2 className="text-3xl md:text-4xl font-title text-primary mb-4 tracking-tight leading-snug">
							L'Arte di gustare la birra
						</h2>
						<p className="text-white text-base md:text-lg leading-relaxed mb-6">
							Schiuma perfetta, bicchiere inclinato, ossigenazione ideale: ogni dettaglio fa la differenza.
							<br />
							Scopri come serviamo la birra per esaltare ogni sua sfumatura.
						</p>
						<button
							onClick={() =>
								navigate("/menu", {
									state: { preselectedCategoryId: "c91890ae-e0e1-40f8-9c6a-8bf940b351fa" },
								})
							}
							className="mt-4 px-6 py-3 rounded-full bg-primary text-black font-semibold hover:scale-105 transition"
						>
							Scopri le nostre birre
						</button>
					</div>
				</motion.div>
			</section>

			{/* Partner */}

			<PartnerCarousel />

			{/* Recensioni */}

			<CustomerReviews />

			{/* Contatti */}
			<section className="px-6 py-20 bg-surface text-muted text-center">
				<motion.h2
					className="text-2xl font-title text-primary mb-4 tracking-tight leading-snug"
					initial={{ opacity: 0, y: 10 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.4 }}
					viewport={{ once: true, amount: 0.1 }}
				>
					Vieni a trovarci
				</motion.h2>

				{/* Indirizzo e telefono */}
				<motion.div
					className="flex flex-col items-center gap-2 mb-6 text-sm"
					initial={{ opacity: 0 }}
					whileInView={{ opacity: 1 }}
					transition={{ delay: 0.2, duration: 0.3 }}
					viewport={{ once: true, amount: 0.1 }}
				>
					<p className="flex items-center gap-2">
						<MapPin className="w-4 h-4 text-primary" />
						<a href="https://goo.gl/maps/f7yhpAyxAPCeKrDL9" target="_blank" rel="noreferrer" className="hover:underline text-secondary">
							Via Piave, 13 - Avetrana (TA)
						</a>
					</p>

					<p className="flex items-center gap-2">
						<Phone className="w-4 h-4 text-primary" />
						<a href="tel:+393501175128" className="hover:underline text-secondary">
							+39 350 117 5128
						</a>
					</p>
				</motion.div>

				{/* Divider */}
				<div className="w-10 border-t border-gray-500 mx-auto my-6"></div>

				{/* Orari */}
				<motion.div
					className="flex flex-col items-center gap-2 text-sm"
					initial={{ opacity: 0 }}
					whileInView={{ opacity: 1 }}
					transition={{ delay: 0.3, duration: 0.4 }}
					viewport={{ once: true, amount: 0.1 }}
				>
					<p className="flex items-center gap-2">
						<Clock className="w-4 h-4 text-primary" />
						Orari: <span className="text-secondary">19:00 - 00:00</span>
					</p>

					<p className="flex items-center gap-2">
						<CalendarX className="w-4 h-4 text-primary" />
						<span>
							Chiusura settimanale:{" "}
							<strong className="text-secondary font-semibold">Mercoledì</strong>
						</span>
					</p>
				</motion.div>

				{/* Mappa */}
				<motion.div
					initial={{ opacity: 0 }}
					whileInView={{ opacity: 1 }}
					transition={{ delay: 0.5, duration: 0.6 }}
					className="mt-10 border-2 border-primary rounded-2xl overflow-hidden shadow-lg max-w-4xl mx-auto"
				>
					<iframe
						src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3040.628136711098!2d17.727625899999996!3d40.35059450000001!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1346925aa936640b%3A0xca65ff705b18a0a7!2sPizzeria%20Il%20Balconcino%20Food%20%26%20Beer!5e0!3m2!1sit!2sit!4v1751275674372!5m2!1sit!2sit"
						title="map"
						allowFullScreen=""
						loading="lazy"
						className="w-full h-64 border-0 map"
					/>
				</motion.div>
			</section>

			{/* Footer */}
			<footer className="bg-background border-t border-accent text-center text-xs text-muted py-10 px-4 relative">
				{/* Divider visivo per stacco */}
				<div className="absolute top-[-2px] left-1/2 -translate-x-1/2 w-full bg-primary rounded-full shadow-lg" />

				{/* Social */}
				<div className="flex justify-center gap-6 mb-6">
					<a
						href="https://www.facebook.com/ilbalconcinoavetrana"
						target="_blank"
						rel="noreferrer"
						className="hover:text-primary transition"
					>
						<Facebook className="w-5 h-5" />
					</a>
					<a
						href="https://www.instagram.com/ilbalconcinofoodbeer/"
						target="_blank"
						rel="noreferrer"
						className="hover:text-primary transition"
					>
						<Instagram className="w-5 h-5" />
					</a>
				</div>

				<div className="flex justify-center gap-6 mb-6">
					© {new Date().getFullYear()} IL BALCONCINO FOOD & BEER - Tutti i diritti riservati
				</div>


				{/* Powered by 
				<p className="text-muted mb-1">POWERED BY</p>
				<a href="https://innovalis.it" target="_blank" rel="noreferrer">
					<img src={innovalis} alt="Innovalis" className="mx-auto mt-2 h-6" />
				</a>
				*/}
			</footer>
		</main>
	);
};

export default Home;
